import { useEffect } from 'react';
import { Partytown } from '@qwik.dev/partytown/react';
import { ErrorBoundary } from '@sentry/react';
import { RouterProvider } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { QueryClient, QueryClientProvider } from 'react-query';
import { logTimeToRedirectToAuth } from '@frontend/auth';
import { startWeaveSession, registerTokenStorageEventListener } from '@frontend/auth-helpers';
import { useRemoveAppLoader } from '@frontend/document';
import appConfig, { getInitialParams } from '@frontend/env';
import { http } from '@frontend/fetch';
import { useWeTags } from '@frontend/scope';
import { InterRouterProvider } from '@frontend/settings-routing';
import { ShellTitleBar } from '@frontend/shell-theme';
import { shell } from '@frontend/shell-utils';
import { registerTabSessionId } from '@frontend/tab-management';
import { TracerModal } from '@frontend/tracer-modal';
import { getNavigationStartTime, useSentrySpanMarker } from '@frontend/tracking';
import { setWetagQueryClient } from '@frontend/wetags';
import { ThemeProvider, AlertsProvider, configureModalSettings, Modal } from '@frontend/design-system';
import { TOP_BAR_HEIGHT } from './constants';
import { AppProvider } from './providers/app.provider';
import { RoutesProvider } from './providers/routes.provider';
import { initializeRouter } from './router';
import { getDayJSLocale } from './utils/dayjs-locales';
import { sharedQueryCache } from './utils/query-cache';

// Conditional loading on ENV, is set in the loader function
if (import.meta.env.MODE === 'production') {
  import('./third-party-scripts').then(({ loadThirdPartyScripts }) => {
    loadThirdPartyScripts();
  });
} else {
  console.warn('Third party scripts are not loaded because the app is not running in production.');
}

const router = initializeRouter();

const queryClient = new QueryClient({
  queryCache: sharedQueryCache,
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      /**
       * We are only turning on the error boundary in development mode to avoid issues in prod.
       * When we are ready, we want to turn this on for prod.
       */
      // useErrorBoundary: import.meta.env.MODE === 'development',
    },
  },
});

// This sets the QueryClient used by the wetags library for prefetching, invalidating, etc.
// TODO: We need to have only one instance of QueryClient in the app to avoid cache issues.
// There seems to be two instances right now, one here and one in the `AuthenticatedApp`.
setWetagQueryClient(queryClient);

declare global {
  interface Window {
    Cypress?: any;
  }
}

registerTokenStorageEventListener();
startWeaveSession();
registerTabSessionId();

export default function Root() {
  // This is used to mark the root component as mounted in the Sentry waterfall trace
  // Set the startTime to just after the DOM content is loaded (+1 so it shows after the browser.domContentLoadedEvent in Sentry)
  useSentrySpanMarker('Root mounted', 'ui.react.mounted', performance.timing.domContentLoadedEventEnd + 1);
  // For this timing, set the startTime to when the browser starts handling a request which gives us the complete time till the user can do something in the app
  useSentrySpanMarker('Weave app mounted', 'ui.weaveapp.mounted', getNavigationStartTime());
  const newAlert = useWeTags('new-alert-design');
  useEffect(() => {
    // delay it because it uses pendo which is lazy-loaded
    setTimeout(() => {
      logTimeToRedirectToAuth();
    }, 2000);
  }, []);

  console.log('Weave Assets Version: ', appConfig.VERSION);
  if (shell?.isShell) {
    console.log('Weave Shell Version: ', shell?.version);
  }

  useEffect(() => {
    getDayJSLocale().then((locale) => {
      dayjs.locale(locale);
    });
  }, []);

  useEffect(() => {
    configureModalSettings({
      mountTarget: () => {
        return document.getElementById('vertical-slide-modal-backdrop')?.contains(document.activeElement)
          ? '#vertical-slide-modal-backdrop'
          : undefined;
      },
    });
  }, []);

  if (!http.baseUrl) {
    const { backendApi } = getInitialParams();
    http.setBaseUrl(backendApi);
  }

  const hasShellThemeEnabled = shell?.isShell && shell?.featureAvailability?.has('shell-theme');
  useRemoveAppLoader();
  return (
    <>
      {/* Will need verbose on dev tools set to see logs if debug is true. */}
      <Partytown
        debug={import.meta.env.MODE !== 'production'}
        forward={['dataLayer.push', 'fbq']}
        resolveUrl={function (url) {
          if (url.hostname === 'connect.facebook.net' && import.meta.env.MODE === 'production') {
            const proxyUrl = new URL(self.location.origin + '/api/proxy-api/getexternal');
            proxyUrl.searchParams.append('url', url.href);
            return proxyUrl;
          }
          return url;
        }}
      />
      <ErrorBoundary
        fallback={({ error }) => {
          console.error('ErrorBoundary caught an error:', error);
          return <div>An error occurred: {(error as Error).message}</div>;
        }}
      >
        <AppProvider>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider
              includeEmotionTheme
              skipAnimation={!!window?.Cypress}
              heightOffset={hasShellThemeEnabled ? 42 : undefined}
            >
              {hasShellThemeEnabled && <ShellTitleBar />}
              <RoutesProvider queryClient={queryClient}>
                <InterRouterProvider>
                  <Modal.AppConfigProvider topOffset={TOP_BAR_HEIGHT}>
                    <AlertsProvider variant={newAlert ? 'experimental' : 'default'}>
                      <RouterProvider router={router} />
                    </AlertsProvider>
                  </Modal.AppConfigProvider>
                </InterRouterProvider>
                <TracerModal />
              </RoutesProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </AppProvider>
      </ErrorBoundary>
    </>
  );
}
